import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import MedicationSearch from "./MedicationSearch";

/**
 * `essentialOnly` is for the VisitStart check-in.
 *
 * Same form, three fields instead of six: prescriber, pharmacy and notes are
 * dropped, and dosage and frequency stop being required. A patient standing in a
 * waiting room on a phone is not going to name their pharmacy, and requiring a
 * dosage they do not know is worse than friction — it makes them invent one or
 * abandon the entry, and an invented dose on a chart is a prescribing hazard.
 *
 * The RxNorm lookup on the name stays in both modes: that is what carries the
 * rxcui the drug-allergy interaction check runs on.
 */
/**
 * What the search field starts with when editing. A saved rxcui means it was
 * picked from RxNorm, so it goes back in as that pick, not as a bare name; a
 * string reads as typed by hand, which showed "manual" and dropped the rxcui on
 * save unless the patient searched and picked it again.
 */
const selectionFor = (medication) => {
  if (!medication?.name) return null;
  if (!medication.rxcui) return medication.name;
  return {
    name: medication.name,
    displayName: medication.name,
    rxcui: medication.rxcui,
  };
};

const MedicationForm = ({
  open,
  onClose,
  onSave,
  medication = null,
  isEdit = false,
  essentialOnly = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedMedication, setSelectedMedication] = useState(() =>
    selectionFor(medication)
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      rxcui: "",
      dosage: "",
      frequency: "",
      prescribedBy: "",
      pharmacy: "",
      notes: "",
    },
  });

  // Watch for changes in medication name
  const medicationName = watch("name");

  // Reset form when opening
  useEffect(() => {
    if (open) {
      if (medication) {
        // Ensure all fields have default values (never undefined)
        reset({
          name: medication.name || "",
          rxcui: medication.rxcui || "",
          dosage: medication.dosage || "",
          frequency: medication.frequency || "",
          prescribedBy: medication.prescribedBy || "",
          pharmacy: medication.pharmacy || "",
          notes: medication.notes || "",
        });
        setSelectedMedication(selectionFor(medication));
      } else {
        reset({
          name: "",
          rxcui: "",
          dosage: "",
          frequency: "",
          prescribedBy: "",
          pharmacy: "",
          notes: "",
        });
        setSelectedMedication(null);
      }
    }
  }, [open, medication, reset]);

  // Parse medication name to extract clean name without brackets
  const parseMedicationName = (name) => {
    if (!name) return { cleanName: "", brandName: "" };

    const brandMatch = name.match(/^(.+?)\s*\[([^\]]+)\]$/);
    if (brandMatch) {
      return {
        cleanName: brandMatch[1].trim(),
        brandName: brandMatch[2].trim(),
      };
    }
    return { cleanName: name, brandName: "" };
  };

  // Handle medication selection from search
  const handleMedicationSelect = (selectedMed) => {
    if (selectedMed && typeof selectedMed === "object") {
      // Verified medication selected - store clean name only
      const { cleanName } = parseMedicationName(
        selectedMed.displayName || selectedMed.name
      );
      setValue("name", cleanName);
      setValue("rxcui", selectedMed.rxcui); // Store RxCUI for allergy checking
      setSelectedMedication(selectedMed);
    } else if (typeof selectedMed === "string") {
      // Manual entry or user modified RxNorm medication
      setValue("name", selectedMed);
      setValue("rxcui", ""); // No RxCUI for manual entries
      setSelectedMedication(selectedMed); // Store as string, clearing RxNorm data
    } else {
      // Null/undefined - clear everything
      setValue("name", "");
      setValue("rxcui", "");
      setSelectedMedication(null);
    }
  };

  // Handle form submission
  const onSubmit = async (data) => {
    setLoading(true);
    setSaveError(null);

    try {
      // name, dosage and frequency are always sent, even blank. firestore.rules
      // requires all three KEYS on a medication doc (isValidMedication) while
      // allowing dosage and frequency to be empty strings, so omitting a blank
      // one comes back as "Missing or insufficient permissions" rather than
      // anything that names the real problem. Only reachable under essentialOnly,
      // where the two stop being required fields.
      const medicationData = {
        name: data.name.trim(),
        dosage: (data.dosage || "").trim(),
        frequency: (data.frequency || "").trim(),
      };

      // The genuinely optional fields are still omitted rather than stored blank.
      if (data.rxcui && data.rxcui.trim()) {
        medicationData.rxcui = data.rxcui.trim();
      }

      if (data.prescribedBy && data.prescribedBy.trim()) {
        medicationData.prescribedBy = data.prescribedBy.trim();
      }

      if (data.pharmacy && data.pharmacy.trim()) {
        medicationData.pharmacy = data.pharmacy.trim();
      }

      if (data.notes && data.notes.trim()) {
        medicationData.notes = data.notes.trim();
      }

      // Additional safety check: if we have an rxcui, make sure the medication name
      // actually corresponds to a currently selected verified medication
      if (medicationData.rxcui && typeof selectedMedication === "string") {
        // User manually edited the name after selecting verified medication
        // Clear the rxcui since it's no longer accurate
        delete medicationData.rxcui;
      }

      // Include medication ID when editing
      const dataToSave =
        isEdit && medication?.id
          ? { ...medicationData, id: medication.id }
          : medicationData;

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error("Error saving medication:", error);
      setSaveError(
        error.message || "Failed to save medication. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {isEdit ? "Edit Medication" : "Add New Medication"}
        </DialogTitle>

        <DialogContent>
          {/* mt: 0 cancels the negative top margin `spacing` puts on a Grid
              container, which is what was slicing the first field's label.
              MUI zeroes a DialogContent's top padding when it follows a
              DialogTitle, so the scroll box starts at y=0 and the container's
              -24px put its first row above that; the floating label sits above
              its own border, so the label was the part outside.
              It has to be fixed HERE and not with padding on the DialogContent:
              that rule is `.MuiDialogTitle-root + &`, two classes, so it
              outranks anything sx can generate and the padding is dropped
              silently. Cancelling the margin instead leaves the items' own
              24px top padding as the gap under the title. */}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Medication Name Search */}
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Medication name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                }}
                render={({ field }) => (
                  <MedicationSearch
                    autoFocus
                    label="Medication Name"
                    value={selectedMedication}
                    onChange={field.onChange}
                    onSelect={handleMedicationSelect}
                    error={errors.name}
                    helperText={errors.name?.message}
                    placeholder="Start typing medication name..."
                  />
                )}
              />
            </Grid>

            {/* Dosage and Frequency */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="dosage"
                control={control}
                rules={{
                  required: essentialOnly ? false : "Dosage is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={essentialOnly ? "Dosage (if you know it)" : "Dosage"}
                    type="text"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    placeholder="e.g., 10mg, 1 tablet"
                    error={!!errors.dosage}
                    helperText={errors.dosage?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="frequency"
                control={control}
                rules={{
                  required: essentialOnly ? false : "Frequency is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      essentialOnly ? "How often (if you know it)" : "Frequency"
                    }
                    type="text"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    placeholder="e.g., Once daily, Twice daily"
                    error={!!errors.frequency}
                    helperText={errors.frequency?.message}
                  />
                )}
              />
            </Grid>

            {/* Prescriber, pharmacy and notes: record-keeping the patient can fill
                in later from their own medications page, so the check-in skips them. */}
            {!essentialOnly && (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="prescribedBy"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Prescribed By"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        placeholder="Doctor or prescriber name"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="pharmacy"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Pharmacy"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        placeholder="Enter pharmacy name"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Additional Notes"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Any additional information about this medication..."
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {saveError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {saveError}
            </FormHelperText>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {/* No autoFocus here: it was on this button, so the dialog opened with
              the cursor on Save instead of in the field it is there to fill. */}
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            variant="contained"
          >
            {isEdit ? "Update" : "Save"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MedicationForm;
